import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button, { THEMES } from "common/blocks/Button";
import RichText from "common/blocks/RichText";
import TextInput from "common/blocks/TextInput";
import useEmailSignup from "hooks/useEmailSignup/useEmailSignup";
import { useForm } from "react-hook-form";
import { ResolvedSignupForm } from "resolvers/data/signupFormResolver";

import FormState from "./FormState";
import styles from "./NewsletterForm.module.scss";
import { NewsletterFormSchema } from "./schema";

const cx = classNames.bind(styles);

export interface NewsletterFormProps extends ResolvedSignupForm {
    buttonAriaLabel?: string;
    buttonClassName?: string;
    buttonTheme?: (typeof THEMES)[number];
    formWrapperClassName?: string;
    inputLabel?: string;
}

const NewsletterForm = (props: NewsletterFormProps) => {
    const {
        buttonText = "Sign up",
        buttonAriaLabel = "Submit email address",
        inputLabel,
        inputPlaceholder = "Your email",
        terms,
        buttonTheme,
        successTitle,
        successBody,
        errorTitle,
        errorBody,
        errorTryAgainText,
        validationErrorMessage,
        formWrapperClassName,
        buttonClassName,
    } = props;

    const {
        formState: { errors, isSubmitSuccessful },
        handleSubmit,
        register,
        watch,
        reset,
    } = useForm({
        resolver: zodResolver(NewsletterFormSchema(validationErrorMessage)),
        defaultValues: {
            NewsletterEmail: "",
        },
        mode: "onSubmit",
    });

    const resetForm = () => {
        reset({ NewsletterEmail: "" });
    };

    const { handleAPICall, error } = useEmailSignup();

    if (isSubmitSuccessful && !error)
        return (
            <div className={cx("form")}>
                <FormState icon={"RoundedTick"} title={successTitle} text={successBody} />
            </div>
        );

    if (errors.NewsletterEmail?.type === "error")
        return (
            <div className={cx("form")}>
                <FormState
                    icon={"RoundedError"}
                    title={errorTitle}
                    text={errorBody}
                    isRedirect
                    redirectText={errorTryAgainText}
                    onRedirectClick={resetForm}
                />
            </div>
        );

    return (
        <div className={cx("form")}>
            <form
                className={cx("wrapper", formWrapperClassName)}
                onSubmit={handleSubmit(handleAPICall)}
            >
                <TextInput
                    className={cx("textInput")}
                    errorTextClassName={cx("errorText")}
                    errorIconClassName={cx("errorIcon")}
                    label={inputLabel}
                    placeholder={inputPlaceholder}
                    hasError={!!errors.NewsletterEmail?.type}
                    errorText={errors?.NewsletterEmail?.message}
                    value={watch("NewsletterEmail")}
                    {...register("NewsletterEmail")}
                />
                <div className={cx("buttonWrap")}>
                    <Button
                        type="submit"
                        ariaLabel={buttonAriaLabel}
                        className={cx("button", buttonClassName)}
                        theme={buttonTheme || "primary"}
                    >
                        {buttonText}
                    </Button>
                </div>
            </form>
            {isSubmitSuccessful && error && (
                <div className={cx("formError")}>
                    <FormState
                        icon={"RoundedError"}
                        title={errorTitle}
                        text={errorBody}
                        isRedirect
                        redirectText={errorTryAgainText}
                        onRedirectClick={resetForm}
                    />
                </div>
            )}
            {terms && <RichText className={cx("terms")} {...terms} />}
        </div>
    );
};

export default NewsletterForm;
